
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// Add TypeScript declaration for Vite's preamble property
declare global {
  interface Window {
    __vite_plugin_react_preamble_installed__?: boolean;
  }
}

// Add a class to the body to help with mobile styling
document.addEventListener("DOMContentLoaded", () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile =
    /android|webos|iphone|ipad|ipod|blackberry|windows phone/i.test(userAgent);

  if (isMobile) {
    document.body.classList.add("is-mobile-device");

    // Ensure Lovable badge elements are hidden
    const style = document.createElement("style");
    style.textContent = `
      [data-lovable-badge],
      [data-lovable-editable],
      .lovable-badge,
      .lovable-edit-wrapper,
      #lovable-badge,
      #lovable-editable {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        z-index: -9999 !important;
      }
    `;
    document.head.appendChild(style);
  }
});

// Generate a timestamp for cache busting
const timestamp = Date.now();

// Add timestamp to all dynamic resource URLs
const addTimestampToResources = () => {
  const updateURLs = (selector: string, attribute: string) => {
    const elements = document.querySelectorAll(selector);
    elements.forEach((el) => {
      const url = el.getAttribute(attribute);
      if (url && !url.includes('?') && !url.includes('data:') && !url.startsWith('http')) {
        el.setAttribute(attribute, `${url}?v=${timestamp}`);
      }
    });
  };
  
  // Add timestamp to images, scripts, stylesheets, and other resources
  updateURLs('img', 'src');
  updateURLs('script', 'src');
  updateURLs('link[rel="stylesheet"]', 'href');
  updateURLs('a', 'href');
};

// Apply timestamp to resources when DOM is ready
document.addEventListener('DOMContentLoaded', addTimestampToResources);

// Create root and render app
const rootElement = document.getElementById("root");
if (rootElement) {
  const root = createRoot(rootElement);
  root.render(<App key={timestamp} />); // Add key to force re-render on reload
}

// Cache busting mechanism
const checkForUpdates = () => {
  const cacheBuster = `?cb=${Date.now()}`;
  fetch(`/version.json${cacheBuster}`)
    .then(response => {
      if (!response.ok) throw new Error('Failed to fetch version');
      return response.json();
    })
    .then(data => {
      if (!data) return;
      
      const currentBuildId = localStorage.getItem('app-build-id');
      if (currentBuildId && currentBuildId !== data.buildId) {
        console.log('New version detected, refreshing...');
        localStorage.setItem('app-build-id', data.buildId);
        localStorage.setItem('app-build-time', data.buildTime);
        
        // Hard reload with cache busting
        window.location.href = window.location.href.split('?')[0] + `?refresh=${Date.now()}`;
      } else if (!currentBuildId) {
        localStorage.setItem('app-build-id', data.buildId);
        localStorage.setItem('app-build-time', data.buildTime);
      }
    })
    .catch(err => {
      console.log('Version check error:', err);
    });
};

// Check for updates more frequently - every 15 seconds instead of 30
window.addEventListener('load', () => {
  checkForUpdates();
  setInterval(checkForUpdates, 15000);
});

// Force browser to reload all assets when hard refresh is done
window.addEventListener('beforeunload', () => {
  // Clear all application cache to ensure fresh content on reload
  if (navigator.serviceWorker && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ action: 'clearCache' });
  }
  
  // Clear browser cache data
  localStorage.setItem('last-cache-clear', Date.now().toString());
});

// Add additional cache control headers via meta tags
const addCacheControlMetaTags = () => {
  const metaTags = [
    { name: 'Cache-Control', content: 'no-cache, no-store, must-revalidate' },
    { name: 'Pragma', content: 'no-cache' },
    { name: 'Expires', content: '0' }
  ];
  
  metaTags.forEach(tag => {
    const meta = document.createElement('meta');
    meta.httpEquiv = tag.name;
    meta.content = tag.content;
    document.head.appendChild(meta);
  });
};

document.addEventListener('DOMContentLoaded', addCacheControlMetaTags);
